/**
 * Frontend-only global search service.
 * Calls existing API services in parallel and aggregates/ranks results.
 */
import { domainService } from './domainService';
import { useCaseService } from './useCaseService';
import cadrageService from './cadrageService';

export type SearchResultType = 'domain' | 'use_case' | 'cadrage';

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  url: string;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  total: number;
}

const MAX_PER_CATEGORY = 5;

function matchesQuery(query: string, ...fields: (string | null | undefined)[]): boolean {
  const q = query.toLowerCase().trim();
  return fields.some((f) => f && f.toLowerCase().includes(q));
}

export const searchService = {
  async globalSearch(query: string): Promise<SearchResponse> {
    if (!query.trim()) {
      return { query, results: [], total: 0 };
    }

    // Fetch all three in parallel; gracefully ignore individual failures
    const [domains, useCases, cadrages] = await Promise.allSettled([
      domainService.getUserDomains(),
      useCaseService.list({ limit: 200 }),
      cadrageService.getCadrages(),
    ]);

    const results: SearchResult[] = [];

    // --- Domains ---
    if (domains.status === 'fulfilled') {
      const matched = domains.value
        .filter((d) => matchesQuery(query, d.name, d.description))
        .slice(0, MAX_PER_CATEGORY);

      for (const d of matched) {
        results.push({
          id: String(d.id),
          type: 'domain',
          title: d.name,
          subtitle: d.description ? d.description.slice(0, 80) : undefined,
          url: `/domains/${d.id}`,
        });
      }
    }

    // --- Use Cases ---
    if (useCases.status === 'fulfilled') {
      const matched = useCases.value
        .filter((uc) => matchesQuery(query, uc.name, uc.short_description, uc.status))
        .slice(0, MAX_PER_CATEGORY);

      for (const uc of matched) {
        results.push({
          id: String(uc.id),
          type: 'use_case',
          title: uc.name,
          subtitle: uc.status ?? undefined,
          url: `/cas-usage/${uc.id}`,
        });
      }
    }

    // --- Cadrages ---
    if (cadrages.status === 'fulfilled') {
      const matched = cadrages.value
        .filter((c) => matchesQuery(query, c.name, c.client))
        .slice(0, MAX_PER_CATEGORY);

      for (const c of matched) {
        results.push({
          id: String(c.id),
          type: 'cadrage',
          title: c.name,
          subtitle: c.client ?? undefined,
          url: `/cadrages/${c.id}`,
        });
      }
    }

    return { query, results, total: results.length };
  },
};

export default searchService;
